import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Step 4e — simplified create-new-note flow.
// Just enough to open a new (empty) delivery note. Customer + the two
// dates. Submit → POST /delivery-notes → navigate to the editor where
// staff add skips throughout the day.

const todayIso = () => new Date().toISOString().split('T')[0];
const addOneDay = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return '';
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + 1);
  return dt.toISOString().split('T')[0];
};

const NewDeliveryNote = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [user, setUser] = useState(null);
  const [requestDate, setRequestDate] = useState(todayIso());
  const [deliveryDate, setDeliveryDate] = useState(addOneDay(todayIso()));
  const [customerId, setCustomerId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/api/operations/customers`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setCustomers((d || []).filter(c => c.active !== false)))
      .catch(e => console.error('Failed loading customers:', e));
    fetch(`${API_URL}/api/auth/me`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => setUser(d))
      .catch(() => {});
  }, []);

  // When request_date changes, default delivery_date to (request_date + 1)
  // unless the user has already set a delivery_date earlier than the new
  // request_date (in which case bump it forward).
  const onRequestDateChange = (v) => {
    setRequestDate(v);
    if (!deliveryDate || deliveryDate < v) setDeliveryDate(addOneDay(v));
  };

  const submit = async () => {
    setError('');
    if (!customerId) { setError('Please pick a customer.'); return; }
    if (!requestDate) { setError('Please set the processing date.'); return; }
    if (!deliveryDate) { setError('Please set the delivery date.'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/operations/delivery-notes`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: parseInt(customerId, 10),
          request_date: requestDate,
          delivery_date: deliveryDate,
          ref_number: '',  // deprecated; backend tolerates empty
          created_by: user?.username || 'staff',
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Create failed (${res.status})`);
      }
      const note = await res.json();
      // Land in the editor for the freshly-opened note.
      navigate(`/operations/delivery-notes/${note.id}`);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/operations/delivery-notes')} className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft size={22} />
        </button>
        <h1 className="text-3xl font-bold text-gray-800" style={{ fontFamily: '"Cinzel", serif' }}>
          New Delivery Note
        </h1>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6 max-w-xl">
        <p className="text-sm text-gray-600 mb-5">
          Open a new delivery note for a customer. You'll add skips throughout
          the day on the next screen — once all skips are closed, you can
          complete and email it.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
            <select
              value={customerId} onChange={(e) => setCustomerId(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1F2D3D]"
            >
              <option value="">— select a customer —</option>
              {customers.map(c => (
                <option key={c.id} value={c.id}>{c.code} · {c.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Processing date (request_date)</label>
              <input
                type="date" value={requestDate}
                onChange={(e) => onRequestDateChange(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1F2D3D]"
              />
              <p className="text-xs text-gray-500 mt-1">The day we process the linen at the laundry. Shows on the printed label as "DATE".</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Delivery date</label>
              <input
                type="date" value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1F2D3D]"
              />
              <p className="text-xs text-gray-500 mt-1">The day the customer receives the linen. Defaults to the day after processing.</p>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
          <button
            onClick={() => navigate('/operations/delivery-notes')}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={submit} disabled={submitting}
            className="px-5 py-2 bg-[#1F2D3D] text-white rounded-lg hover:bg-[#1A2332] disabled:opacity-50"
          >
            {submitting ? 'Creating…' : 'Open Delivery Note'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewDeliveryNote;
